import { ErrorMessage, Field, Form, Formik } from "formik";
import { LockSimple } from "@phosphor-icons/react";
import { FC, useState } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useCreatePin from "../hooks/useCreatePin";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const schema = yup.object().shape({
  newPin: yup
    .string()
    .matches(/^\d{4}$/, "Pin must be exactly 4 digits")
    .required("New pin is required"),
  confirmPin: yup
    .string()
    .oneOf([yup.ref("newPin")], "Pins must match")
    .required("Confirm pin is required"),
});

const CreatePinModal: FC<{
  setShowPinModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setShowPinModal }) => {
  const [pinIsVisible, setPinIsVisible] = useState(false);

  const { refetch } = useAuth();

  const { createPin, creatingPin } = useCreatePin({
    onSuccess() {
      toast.success("Transaction Pin created successfully");
      refetch();
      setShowPinModal(false);
    },
    onError() {
      toast.error("An error occurred while creating pin");
    },
  });

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          newPin: "",
          confirmPin: "",
        }}
        onSubmit={(values) => {
          createPin({
            pin: values.newPin,
          });
        }}
      >
        <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
          <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
            <LockSimple size={28} />
          </div>
          <div className="flex items-center flex-col">
            <div className="font-medium">Create Transaction Pin</div>
            <p className="text-gray-500 text-center">
              Enter your pin to use for payments
            </p>
          </div>
          <div className="flex flex-col gap-2 max-w-md w-full">
            <label htmlFor="pin" className="text-sm font-bold text-gray-700">
              New Pin*
            </label>
            <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
              <Field
                autoComplete="off"
                type={pinIsVisible ? "text" : "password"}
                id="newPin"
                name="newPin"
                placeholder="Create a new pin"
                maxLength={4}
                inputMode="numeric"
                pattern="\d*"
                className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
              />
              <div
                onClick={() => setPinIsVisible((prev) => !prev)}
                className="absolute right-0 m-4 cursor-pointer text-sm"
              >
                {pinIsVisible ? "Hide" : "Show"}
              </div>
            </div>
            <ErrorMessage
              name="newPin"
              component={motion.div}
              className="text-red-500"
            />
          </div>
          <div className="flex flex-col gap-2 max-w-md w-full">
            <label htmlFor="pin" className="text-sm font-bold text-gray-700">
              Confirm New Pin*
            </label>
            <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
              <Field
                autoComplete="off"
                type={pinIsVisible ? "text" : "password"}
                id="confirmPin"
                name="confirmPin"
                placeholder="Confirm your new pin"
                maxLength={4}
                inputMode="numeric"
                pattern="\d*"
                className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
              />
              <div
                onClick={() => setPinIsVisible((prev) => !prev)}
                className="absolute right-0 m-4 cursor-pointer text-sm"
              >
                {pinIsVisible ? "Hide" : "Show"}
              </div>
            </div>
            <ErrorMessage
              name="confirmPin"
              component={motion.div}
              className="text-red-500"
            />
          </div>
          <div className="flex items-center gap-5 w-full">
            <button
              type="button"
              aria-label="Cancel"
              onClick={() => setShowPinModal(false)}
              className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingPin}
              className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
            >
              {creatingPin ? "Creating..." : "Save"}
            </button>
          </div>
        </Form>
      </Formik>
    </motion.div>
  );
};

export default CreatePinModal;
